import { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Popover } from '@mui/material';
import { ChevronRight } from 'react-feather';
import type { MessageTestingCell, Score } from '@trinity/chat/types/message-testing';

// ─── Constants ──────────────────────────────────────────────────────────────

export const COLORS = {
  green: { text: '#065f46', bg: '#ecfdf5', border: '#a7f3d0', accent: '#34d399' },
  yellow: { text: '#ffa700', bg: '#fefce8', border: '#fde68a' },
  red: { text: '#dc2626', bg: '#fef2f2', border: '#fecaca', accent: '#f87171', dark: '#991b1b' },
  neutral: { text: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
} as const;

export const SCORE_LABELS: { key: keyof MessageTestingCell['scores']; label: string }[] = [
  { key: 'personaResonance', label: 'Resonance' },
  { key: 'objectiveAlignment', label: 'Objective Alignment' },
  { key: 'categoryFit', label: 'Category Fit' },
  { key: 'predictedEngagement', label: 'Engagement' },
  { key: 'predictedActionLikelihood', label: 'Action Likelihood' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'credibility', label: 'Credibility' },
  { key: 'cognitiveLoad', label: 'Cognitive Load' },
  { key: 'differentiation', label: 'Differentiation' },
  { key: 'ctaStrength', label: 'CTA Strength' },
  { key: 'toneFit', label: 'Tone Fit' },
];

// ─── Color helpers ──────────────────────────────────────────────────────────

export function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function scoreColor(score: number): string {
  if (score >= 70) return COLORS.green.text;
  if (score >= 50) return COLORS.yellow.text;
  return COLORS.red.text;
}

export function scoreBg(score: number): string {
  if (score >= 70) return COLORS.green.bg;
  if (score >= 50) return COLORS.yellow.bg;
  return COLORS.red.bg;
}

export function scoreBorder(score: number): string {
  if (score >= 70) return COLORS.green.border;
  if (score >= 50) return COLORS.yellow.border;
  return COLORS.red.border;
}

export function severityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return COLORS.red.dark;
    case 'high':
      return COLORS.red.text;
    case 'medium':
      return COLORS.yellow.text;
    case 'low':
      return COLORS.green.text;
    default:
      return '#666';
  }
}

function impactBorderColor(impact: string): string {
  switch (impact) {
    case 'positive':
      return COLORS.green.accent;
    case 'negative':
      return COLORS.red.accent;
    default:
      return COLORS.neutral.border;
  }
}

export function computeOverallScore(scores: MessageTestingCell['scores']): number {
  const values = Object.values(scores).map((s: Score) => s.value);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

// ─── Score Badge ────────────────────────────────────────────────────────────

export function ScoreBadge({ cell }: { cell: MessageTestingCell }): React.JSX.Element {
  const overall = computeOverallScore(cell.scores);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <BadgeContainer $score={overall} ref={anchorRef} onClick={handleOpen}>
        <BadgeCircle $score={overall} />
        <BadgeScoreLabel $score={overall}>({overall})</BadgeScoreLabel>
      </BadgeContainer>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'center', horizontal: 'center' }}
        marginThreshold={16}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 520,
              maxHeight: 'calc(100vh - 32px)',
              overflow: 'auto',
              borderRadius: '10px',
            },
          },
        }}
      >
        <PopoverContent>
          {/* Header */}
          <PopoverHeader>
            <PopoverVerdict $verdict={cell.overallAssessment.verdict}>
              {toTitleCase(cell.overallAssessment.verdict.replace(/_/g, ' '))}
            </PopoverVerdict>
            <PopoverScoreGroup>
              <PopoverCircle $score={overall} />
              <PopoverScoreValue $score={overall}>({overall})</PopoverScoreValue>
            </PopoverScoreGroup>
          </PopoverHeader>
          <PopoverSummary>{cell.overallAssessment.oneLineSummary}</PopoverSummary>

          {/* Strengths & Weaknesses */}
          <PopoverSection>
            <PopoverCallout $type="strength">
              <strong>Top Strength:</strong> {cell.overallAssessment.topStrength}
            </PopoverCallout>
            <PopoverCallout $type="weakness">
              <strong>Top Weakness:</strong> {cell.overallAssessment.topWeakness}
            </PopoverCallout>
          </PopoverSection>

          {/* Scores Grid */}
          <PopoverSectionTitle>Scores</PopoverSectionTitle>
          <ScoresColumns>
            {SCORE_LABELS.map(({ key, label }) => (
              <ScoreItemPopover key={key} label={label} score={cell.scores[key]} />
            ))}
          </ScoresColumns>

          {/* Persona Attributes */}
          {/* {cell.attributeUtilization.invoked.length > 0 && (
            <>
              <PopoverSectionTitle>Resonated Attributes</PopoverSectionTitle>
              <ChipList>
                {cell.attributeUtilization.invoked.map((attr) => (
                  <AttributeChip key={attr} $type="invoked">
                    {attr}
                  </AttributeChip>
                ))}
              </ChipList>
            </>
          )} */}

          {/* Objections */}
          {cell.objections.length > 0 && (
            <CollapsibleSection>
              <CollapsibleSummary>Objections ({cell.objections.length})</CollapsibleSummary>
              <PopoverSection>
                {cell.objections.map((obj, i) => (
                  <ObjectionItem key={i}>
                    <ObjectionTitle>
                      {obj.category.replace(/_/g, ' ')}
                      <ObjectionSeverity $severity={obj.severity}>({obj.severity})</ObjectionSeverity>
                    </ObjectionTitle>
                    <PopoverDetail>{obj.text}</PopoverDetail>
                    {obj.suggestedRebuttal && (
                      <PopoverDetail>
                        <strong>Rebuttal:</strong> {obj.suggestedRebuttal}
                      </PopoverDetail>
                    )}
                  </ObjectionItem>
                ))}
              </PopoverSection>
            </CollapsibleSection>
          )}

          {/* Improvement Suggestions */}
          {cell.improvementSuggestions.length > 0 && (
            <CollapsibleSection>
              <CollapsibleSummary>
                Suggested Improvements ({cell.improvementSuggestions.length})
              </CollapsibleSummary>
              <PopoverSection>
                {cell.improvementSuggestions.map((sug, i) => (
                  <PopoverDetail key={i}>
                    <strong>{toTitleCase(sug.predictedScoreLift)} Impact:</strong> {sug.change}
                    <br />
                    <span style={{ color: '#888', fontSize: '10px' }}>{sug.rationale}</span>
                  </PopoverDetail>
                ))}
              </PopoverSection>
            </CollapsibleSection>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}

// ─── Score Item Popover ─────────────────────────────────────────────────────

function ScoreItemPopover({ label, score }: { label: string; score: Score }): React.JSX.Element {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <ScoreCompactItem ref={anchorRef} onClick={handleOpen} style={{ cursor: 'pointer' }}>
        <ScoreCompactLabel>{label}</ScoreCompactLabel>
        <ScoreCompactRight>
          <ScoreCompactValue $score={score.value}>({score.value})</ScoreCompactValue>
          <ScoreCompactDot $score={score.value} />
        </ScoreCompactRight>
      </ScoreCompactItem>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 360,
              borderRadius: '10px',
            },
          },
        }}
      >
        <ScorePopoverContent>
          <ScorePopoverHeader>
            <ScorePopoverLabel>{label}</ScorePopoverLabel>
            <ScorePopoverGroup>
              <ScorePopoverDot $score={score.value} />
              <ScorePopoverValue $score={score.value}>({score.value})</ScorePopoverValue>
            </ScorePopoverGroup>
          </ScorePopoverHeader>

          <ScoreConfidence>
            <strong>Confidence:</strong>{' '}
            {score.confidence >= 0.7 ? 'High' : score.confidence >= 0.4 ? 'Medium' : 'Low'}
          </ScoreConfidence>

          <ScoreReasoning>{score.reasoning}</ScoreReasoning>

          {score.evidence.length > 0 && (
            <>
              <ScorePopoverSectionTitle>Evidence</ScorePopoverSectionTitle>
              {score.evidence.map((e, i) => (
                <EvidenceItem key={i} $impact={e.impact}>
                  <EvidenceQuote>&ldquo;{e.quote}&rdquo;</EvidenceQuote>
                  <EvidenceMeta>
                    <EvidenceSource>{e.source.replace(/([A-Z])/g, ' $1').trim()}</EvidenceSource>
                  </EvidenceMeta>
                </EvidenceItem>
              ))}
            </>
          )}

          {score.personaAttributesUsed.length > 0 && (
            <>
              <ScorePopoverSectionTitle>Persona Attributes</ScorePopoverSectionTitle>
              <ChipList>
                {score.personaAttributesUsed.map((attr) => (
                  <AttributeChip key={attr} $type="invoked">
                    {attr}
                  </AttributeChip>
                ))}
              </ChipList>
            </>
          )}
        </ScorePopoverContent>
      </Popover>
    </>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const BadgeContainer = styled.div<{ $score: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  min-height: 48px;
  cursor: pointer;
  background: ${(p) => scoreBg(p.$score)};
  border-radius: 4px;
  margin: 4px;
  border: 1px solid ${(p) => scoreBorder(p.$score)};
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 0 0 2px ${(p) => scoreBorder(p.$score)};
  }
`;

const BadgeCircle = styled.div<{ $score: number }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const BadgeScoreLabel = styled.div<{ $score: number }>`
  font-size: 9px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
  margin-top: 2px;
`;

const BadgeHint = styled.div`
  font-size: 9px;
  letter-spacing: 0.3px;
  margin-top: 4px;
  color: #999;
`;

const PopoverContent = styled.div`
  padding: 16px;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const PopoverVerdict = styled.div<{ $verdict: string }>`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const PopoverCircle = styled.div<{ $score: number }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const PopoverScoreGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PopoverScoreValue = styled.span<{ $score: number }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
`;

const PopoverSummary = styled.div`
  font-size: 12px;
  color: #555;
  line-height: 1.4;
  margin-bottom: 12px;
`;

const PopoverSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--black-l);
  margin: 12px 0 6px;
  padding-top: 8px;
  border-top: 1px solid #eee;
`;

const PopoverSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PopoverCallout = styled.div<{ $type: 'strength' | 'weakness' }>`
  font-size: 11px;
  line-height: 1.4;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${(p) => (p.$type === 'strength' ? COLORS.green.bg : COLORS.red.bg)};

  strong {
    font-weight: 600;
  }
`;

const PopoverDetail = styled.div`
  font-size: 11px;
  color: #555;
  line-height: 1.4;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const ScoresColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 12px;
`;

const ScoreCompactLabel = styled.div`
  font-size: 10px;
  color: #666;
  transition: color 0.1s ease;
`;

const ScoreCompactValue = styled.span<{ $score: number }>`
  font-size: 9px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
  opacity: 0;
  transition: opacity 0.1s ease;
`;

const ScoreCompactRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScoreCompactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 0;

  &:hover ${ScoreCompactLabel} {
    color: var(--hyperlink);
    text-decoration: underline;
  }

  &:hover ${ScoreCompactValue} {
    opacity: 1;
  }
`;

const ScoreCompactDot = styled.div<{ $score: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const ScorePopoverContent = styled.div`
  padding: 14px;
`;

const ScorePopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const ScorePopoverLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
`;

const ScorePopoverDot = styled.div<{ $score: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(p) => scoreColor(p.$score)};
`;

const ScorePopoverGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScorePopoverValue = styled.span<{ $score: number }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => scoreColor(p.$score)};
`;

const ScoreConfidence = styled.div`
  font-size: 11px;
  color: #666;
  margin-bottom: 6px;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const ScoreReasoning = styled.div`
  font-size: 11px;
  color: #555;
  line-height: 1.45;
  margin-bottom: 4px;
`;

const ScorePopoverSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #999;
  margin: 10px 0 5px;
  padding-top: 6px;
  border-top: 1px solid #eee;
`;

const EvidenceItem = styled.div<{ $impact: string }>`
  padding: 5px 8px;
  margin: 3px 0;
  border-left: 3px solid ${(p) => impactBorderColor(p.$impact)};
  background: #fafafa;
  border-radius: 0 4px 4px 0;
`;

const EvidenceQuote = styled.div`
  font-size: 11px;
  font-style: italic;
  color: #333;
  line-height: 1.4;
`;

const EvidenceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
`;

const EvidenceSource = styled.span`
  font-size: 9px;
  color: #888;
  text-transform: capitalize;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const AttributeChip = styled.span<{ $type: 'invoked' | 'unused' }>`
  font-size: 10px;
  padding: 4px 6px;
  border-radius: 10px;
  background: ${(p) => (p.$type === 'invoked' ? 'rgb(229, 245, 252)' : COLORS.neutral.bg)};
  color: ${(p) => (p.$type === 'invoked' ? 'var(--black-l)' : COLORS.neutral.text)};
`;

const CollapsibleSection = styled.details`
  margin-top: 8px;
`;

const StyledSummary = styled.summary`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #999;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
  display: flex;
  align-items: center;
  list-style: none;
  border-bottom: 1px solid #eee;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: #666;
  }
`;

const SummaryChevron = styled(ChevronRight)`
  margin-left: auto;
  flex-shrink: 0;
  color: #ccc;
  transition: transform 0.15s ease;

  details[open] > summary > & {
    transform: rotate(90deg);
  }
`;

function CollapsibleSummary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <StyledSummary>
      {children}
      <SummaryChevron size={12} />
    </StyledSummary>
  );
}

const ObjectionItem = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

const ObjectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #444;
  text-transform: capitalize;
  margin-bottom: 2px;
`;

const ObjectionSeverity = styled.span<{ $severity: string }>`
  font-style: italic;
  font-weight: 500;
  color: ${(p) => severityColor(p.$severity)};
  margin-left: 4px;
`;
